import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useTenant } from '../../contexts/TenantContext'
import { supabase } from '../../lib/supabase'
import { Course, CourseLesson } from '../../lib/types'
import { MainLayout } from '../../components/layouts/MainLayout'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { ArrowLeft, Plus, Edit, Trash2, Save, X } from 'lucide-react'

export function LessonsManagePage() {
  useDocumentTitle('レッスン管理')
  const { courseId } = useParams<{ courseId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { currentTenant, memberships } = useTenant()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [course, setCourse] = useState<Course | null>(null)
  const [lessons, setLessons] = useState<CourseLesson[]>([])
  const [editingLesson, setEditingLesson] = useState<CourseLesson | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    lesson_type: 'text' as 'text' | 'pdf' | 'video_link',
    text_content: '',
    pdf_url: '',
    video_link: '',
  })

  useEffect(() => {
    const isTenantAdmin = memberships.some(
      (m) => m.tenant_id === currentTenant?.tenant_id && m.role_in_tenant === 'tenant_admin'
    )

    if (!isTenantAdmin && memberships.length > 0) {
      navigate('/mypage', { replace: true })
      return
    }

    if (courseId && user && currentTenant) {
      fetchCourseAndLessons()
    }
  }, [courseId, user, currentTenant, memberships])

  const fetchCourseAndLessons = async () => {
    if (!courseId || !currentTenant) return

    try {
      setLoading(true)
      setError(null)

      // 講座情報を取得
      const { data: courseData, error: courseError } = await supabase
        .from('courses')
        .select('*')
        .eq('course_id', courseId)
        .eq('tenant_id', currentTenant.tenant_id)
        .single()

      if (courseError) throw courseError
      setCourse(courseData)

      // レッスン一覧を取得
      const { data: lessonsData, error: lessonsError } = await supabase
        .from('course_lessons')
        .select('*')
        .eq('course_id', courseId)
        .order('sort_order', { ascending: true })

      if (lessonsError) throw lessonsError
      setLessons(lessonsData || [])
    } catch (err: any) {
      console.error('Error fetching course and lessons:', err)
      setError(err.message || 'データの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveLesson = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!courseId || !currentTenant) return

    try {
      const lessonData = {
        course_id: courseId,
        tenant_id: currentTenant.tenant_id,
        title: formData.title,
        description: formData.description || null,
        lesson_type: formData.lesson_type,
        text_content: formData.lesson_type === 'text' ? formData.text_content : null,
        pdf_url: formData.lesson_type === 'pdf' ? formData.pdf_url : null,
        video_link: formData.lesson_type === 'video_link' ? formData.video_link : null,
        sort_order: editingLesson ? editingLesson.sort_order : lessons.length + 1,
      }

      if (editingLesson) {
        // 更新
        const { error: updateError } = await supabase
          .from('course_lessons')
          .update(lessonData)
          .eq('lesson_id', editingLesson.lesson_id)

        if (updateError) throw updateError
      } else {
        // 新規作成
        const { error: insertError } = await supabase
          .from('course_lessons')
          .insert([lessonData])

        if (insertError) throw insertError
      }

      // リセット
      setFormData({
        title: '',
        description: '',
        lesson_type: 'text',
        text_content: '',
        pdf_url: '',
        video_link: '',
      })
      setEditingLesson(null)
      setShowForm(false)
      fetchCourseAndLessons()
    } catch (err: any) {
      console.error('Error saving lesson:', err)
      setError(err.message || 'レッスンの保存に失敗しました')
    }
  }

  const handleEditLesson = (lesson: CourseLesson) => {
    setEditingLesson(lesson)
    setFormData({
      title: lesson.title,
      description: lesson.description || '',
      lesson_type: lesson.lesson_type,
      text_content: lesson.text_content || '',
      pdf_url: lesson.pdf_url || '',
      video_link: lesson.video_link || '',
    })
    setShowForm(true)
  }

  const handleDeleteLesson = async (lessonId: string) => {
    if (!confirm('このレッスンを削除してもよろしいですか？')) return

    try {
      const { error: deleteError } = await supabase
        .from('course_lessons')
        .delete()
        .eq('lesson_id', lessonId)

      if (deleteError) throw deleteError

      fetchCourseAndLessons()
    } catch (err: any) {
      console.error('Error deleting lesson:', err)
      setError(err.message || 'レッスンの削除に失敗しました')
    }
  }

  if (loading) {
    return (
      <MainLayout>
        <Loading message="教材を読み込み中..." />
      </MainLayout>
    )
  }

  if (!course) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ErrorMessage message="講座が見つかりませんでした" />
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/tenant/courses')}
            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-500"
          >
            <ArrowLeft className="h-4 w-4" />
            講座一覧へ戻る
          </button>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{course.title}</h1>
          <p className="mt-2 text-gray-600">教材管理</p>
        </div>

        {error && (
          <div className="mb-6">
            <ErrorMessage message={error} />
          </div>
        )}

        {/* レッスン一覧 */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">レッスン一覧</h2>
            <button
              onClick={() => {
                setEditingLesson(null)
                setFormData({
                  title: '',
                  description: '',
                  lesson_type: 'text',
                  text_content: '',
                  pdf_url: '',
                  video_link: '',
                })
                setShowForm(true)
              }}
              className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition text-sm font-medium"
            >
              <Plus className="h-4 w-4" />
              新規レッスン追加
            </button>
          </div>

          {lessons.length === 0 ? (
            <p className="text-center text-gray-500 py-8">まだレッスンがありません</p>
          ) : (
            <div className="space-y-2">
              {lessons.map((lesson, index) => (
                <div
                  key={lesson.lesson_id}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-md hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <span className="text-sm text-gray-500">#{index + 1}</span>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{lesson.title}</h3>
                      <p className="text-sm text-gray-600">
                        {lesson.lesson_type === 'text' && 'テキスト'}
                        {lesson.lesson_type === 'pdf' && 'PDF'}
                        {lesson.lesson_type === 'video_link' && '動画'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditLesson(lesson)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded transition"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteLesson(lesson.lesson_id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* レッスンフォーム */}
        {showForm && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingLesson ? 'レッスン編集' : '新規レッスン'}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false)
                  setEditingLesson(null)
                }}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded transition"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSaveLesson} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  レッスン名 <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">説明</label>
                <textarea
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  教材タイプ <span className="text-red-600">*</span>
                </label>
                <select
                  value={formData.lesson_type}
                  onChange={(e) =>
                    setFormData({ ...formData, lesson_type: e.target.value as any })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="text">テキスト</option>
                  <option value="pdf">PDF</option>
                  <option value="video_link">動画リンク</option>
                </select>
              </div>

              {formData.lesson_type === 'text' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    テキスト内容
                  </label>
                  <textarea
                    rows={10}
                    value={formData.text_content}
                    onChange={(e) => setFormData({ ...formData, text_content: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder="HTMLタグが使用できます"
                  />
                </div>
              )}

              {formData.lesson_type === 'pdf' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PDF URL
                  </label>
                  <input
                    type="url"
                    value={formData.pdf_url}
                    onChange={(e) => setFormData({ ...formData, pdf_url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com/lesson.pdf"
                  />
                </div>
              )}

              {formData.lesson_type === 'video_link' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    動画URL（YouTube埋め込み用）
                  </label>
                  <input
                    type="url"
                    value={formData.video_link}
                    onChange={(e) => setFormData({ ...formData, video_link: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://www.youtube.com/embed/..."
                  />
                </div>
              )}

              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition font-medium"
                >
                  <Save className="h-5 w-5" />
                  保存
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false)
                    setEditingLesson(null)
                  }}
                  className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition"
                >
                  キャンセル
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </MainLayout>
  )
}
